import { getCurrentInstance } from 'vue';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { useAuthStore } from '@/stores/auth';
import { bnHttpErrorHandler } from '@/common/api/http.error.notifications';
import { transformFhirResponse } from '@/common/api/transformFhirResponse';
import { wrapWithDedup, clearDedupCache } from '@/common/api/requestDedup';

var $httpFhirApi;
const timeoutValue = 90000;
export const makeHttpFhir = () => {
    let axiosConfig = {
        headers: {
            post: { Accept: 'application/json' },
            // prevent browsers from storing data in persistant storage, only memory
            // in cases where browsers store "no-cache" data in memory, advise it to validate it hasn't changed
            get: { Accept: 'application/json', 'cache-control': 'no-cache,no-store,must-revalidate,private', expires: 0, pragma: 'no-cache' },
            put: { Accept: 'application/json' },
            patch: { Accept: 'application/json' },
        },
        timeout: !import.meta.env.VITE_NO_TIMEOUT ? timeoutValue : 0,
    };

    if (window.location.host.includes('loca.lt')) {
        axiosConfig.baseURL = 'https://' + import.meta.env.VITE_MOBILE_TEST_NAME + '-bestnotes-fresh-aidbox.loca.lt';
    }

    var httpFhir = axios.create(axiosConfig);

    axiosRetry(httpFhir, { retries: 3 });

    // Check if data is a JSON Patch (RFC 6902) - array of patch operations
    const isJsonPatch = (data) => {
        return Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'op' in data[0];
    };

    // Helper to check if a value is empty (for patch operations)
    const isEmptyPatchValue = (value) => {
        if (value === null || value === undefined || value === '') return true;
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === 'object') return Object.keys(value).length === 0;
        return false;
    };

    // Handle empty 'add' operations in JSON Patch by removing them instead of converting to 'remove'
    // This avoids "Nothing to remove" errors when the property doesn't exist
    const handleEmptyAddOperations = (data) => {
        if (!isJsonPatch(data)) {
            return data;
        }

        return data.filter((item) => {
            if (typeof item === 'object' && item !== null) {
                // Filter out 'add' operations with no value or empty value
                // This prevents "Nothing to remove" errors when converting to 'remove'
                if (item.op === 'add' && (!('value' in item) || isEmptyPatchValue(item.value))) {
                    return false; // Filter out this operation
                }
                // Filter out 'remove' operations that might fail (soft try to remove)
                // We'll let the server handle it gracefully if the path doesn't exist
                if (item.op === 'remove') {
                    // Keep remove operations - server should handle missing paths gracefully
                    // But if we want to be extra safe, we could filter these out too
                    return true;
                }
            }
            return true; // Keep all other operations
        });
    };

    // Helper function to clean up data by removing empty values
    const cleanEmptyValues = (obj) => {
        if (obj === null || obj === undefined || obj === '') {
            return undefined;
        }

        if (Array.isArray(obj)) {
            // Filter out empty values from arrays
            const cleaned = obj
                .map((item) => cleanEmptyValues(item))
                .filter((item) => {
                    if (item === null || item === undefined || item === '') return false;
                    if (Array.isArray(item)) return item.length > 0;
                    if (typeof item === 'object') {
                        return Object.keys(item).length > 0;
                    }
                    return true;
                });
            return cleaned.length > 0 ? cleaned : undefined;
        }

        if (typeof obj === 'object' && obj !== null) {
            const cleaned = {};
            let hasValues = false;

            if (typeof obj.toJSON === 'function') {
                obj = obj.toJSON();
            }

            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const cleanedValue = cleanEmptyValues(obj[key]);
                    if (cleanedValue !== undefined) {
                        cleaned[key] = cleanedValue;
                        hasValues = true;
                    }
                }
            }

            return hasValues ? cleaned : undefined;
        }

        return obj;
    };

    // Add a request interceptor
    httpFhir.interceptors.request.use(
        (config) => {
            const authStore = useAuthStore();

            if (authStore.token && !config?.url?.startsWith('https://bnprofile.fhir') && (!config?.terminology || import.meta.env.VITE_TERMINOLOGY_LOCAL)) {
                config.headers.setAuthorization('Bearer ' + authStore.token);
            }

            // If request is not a terminology request or we have TERMINOLOGY_LOCAL true, set baseURL
            if (!config?.terminology || import.meta.env.VITE_TERMINOLOGY_LOCAL) {
                config.baseURL = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri;
            }
            // Clean up data for POST, PATCH, and PUT requests
            if (['post', 'patch', 'put'].includes(config.method) && config.data) {
                /* global __FHIR_SCHEMA_VALIDATION__ */
                const shouldClean = __FHIR_SCHEMA_VALIDATION__ || authStore.account.fhirApiUri?.includes('fhir-sd.cloud');
                let cleanedData = shouldClean ? cleanEmptyValues(config.data) : config.data;

                cleanedData = shouldClean ? handleEmptyAddOperations(cleanedData) : cleanedData;

                config.data = cleanedData;
            }

            return config;
        },
        (err) => {
            // Do something with request error
            return Promise.reject(err);
        },
    );

    // Add a response interceptor
    httpFhir.interceptors.response.use(
        (res) => transformFhirResponse(res),
        async (err) => {
            return await bnHttpErrorHandler(err, timeoutValue);
        },
    );

    httpFhir.get = wrapWithDedup(httpFhir.get.bind(httpFhir));

    return httpFhir;
};

$httpFhirApi = makeHttpFhir();

export const updateHttpFhirService = () => {
    clearDedupCache();
    $httpFhirApi = makeHttpFhir();
    let app = getCurrentInstance();

    if (app?.appContext) {
        app.appContext.config.globalProperties.$httpFhirApi = $httpFhirApi; // update here whenever $fhirApi changes (token, apiUri)
    }
};

export { $httpFhirApi };
